import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { db, storage } from '../services/firebase';
import { Upload, CheckCircle, AlertCircle } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, collection, addDoc } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

interface Documents {
  idCard: File | null;
  addressProof: File | null;
}

const ProfileSettings = () => {
  const navigate = useNavigate();
  const { user, refreshUserData } = useAuthStore();
  const [documents, setDocuments] = useState<Documents>({
    idCard: null,
    addressProof: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  if (!user) {
    navigate('/login');
    return null;
  }

  const uploadFile = async (file: File, type: string): Promise<string> => {
    const fileExtension = file.name.split('.').pop();
    const fileName = `${user.id}_${type}_${uuidv4()}.${fileExtension}`;
    const storageRef = ref(storage, `verification_documents/${user.id}/${fileName}`);
    
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  };

  const handleFileUpload = (type: keyof Documents) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setDocuments(prev => ({
        ...prev,
        [type]: event.target.files![0]
      }));
    }
  };

  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!documents.idCard && !documents.addressProof) {
        throw new Error('En az bir belge yüklemelisiniz.');
      }

      const uploads = [];
      
      if (documents.idCard) {
        try {
          const idCardUrl = await uploadFile(documents.idCard, 'idCard');
          uploads.push({
            type: 'idCard',
            url: idCardUrl
          });
        } catch (err) {
          console.error('ID Card upload error:', err);
          throw new Error('Kimlik belgesi yüklenirken hata oluştu.');
        }
      }
      
      if (documents.addressProof) {
        try {
          const addressProofUrl = await uploadFile(documents.addressProof, 'addressProof');
          uploads.push({
            type: 'addressProof',
            url: addressProofUrl
          });
        } catch (err) {
          console.error('Address proof upload error:', err);
          throw new Error('Adres belgesi yüklenirken hata oluştu.');
        }
      }

      // Add verification documents
      for (const doc of uploads) {
        await addDoc(collection(db, 'verification_documents'), {
          userId: user.id,
          userEmail: user.email,
          documentType: doc.type,
          fileUrl: doc.url,
          status: 'pending',
          uploadedAt: new Date()
        });
      }

      // Update user verification status
      const userRef = doc(db, 'users', user.id);
      await updateDoc(userRef, {
        verificationStatus: 'pending'
      });

      await refreshUserData();
      setSuccess('Doğrulama belgeleri başarıyla gönderildi. İnceleme sonrası size bilgi verilecektir.');
      
      // Reset form
      setDocuments({
        idCard: null,
        addressProof: null
      });
      
      setTimeout(() => {
        setSuccess(null);
        navigate('/wallet');
      }, 2000);
    } catch (err) {
      console.error('Verification error:', err);
      setError(err instanceof Error ? err.message : 'Belgeler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          Profil Ayarları
        </h1>

        <div className="space-y-6">
          {/* Kullanıcı Bilgileri */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Hesap Bilgileri
            </h2>
            <div className="space-y-2">
              <p className="text-gray-600 dark:text-gray-300">
                <span className="font-medium">Ad Soyad:</span> {user.name}
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                <span className="font-medium">E-posta:</span> {user.email}
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                <span className="font-medium">Hesap Türü:</span> {user.accountType === 'demo' ? 'Demo Hesap' : 'Gerçek Hesap'}
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                <span className="font-medium">Doğrulama Durumu:</span>{' '}
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  user.verificationStatus === 'verified'
                    ? 'bg-green-100 text-green-800'
                    : user.verificationStatus === 'pending'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {user.verificationStatus === 'verified'
                    ? 'Doğrulanmış'
                    : user.verificationStatus === 'pending'
                    ? 'İnceleniyor'
                    : 'Doğrulanmamış'}
                </span>
              </p>
            </div>
          </div>

          {/* Doğrulama Belgeleri */}
          {user.accountType === 'real' && user.verificationStatus !== 'verified' && (
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Kimlik Doğrulama
              </h2>

              {error && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {success && (
                <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-600 dark:text-green-400">{success}</p>
                </div>
              )}

              <form onSubmit={handleVerificationSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Kimlik Belgesi
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      id="idCard"
                      accept="image/*,.pdf"
                      onChange={handleFileUpload('idCard')}
                      className="hidden"
                    />
                    <label
                      htmlFor="idCard"
                      className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer ${
                        documents.idCard
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                          : 'border-gray-300 hover:border-blue-500 dark:border-gray-600 dark:hover:border-blue-400'
                      }`}
                    >
                      {documents.idCard ? (
                        <>
                          <CheckCircle className="w-8 h-8 text-green-500 mb-2" />
                          <span className="text-sm text-green-600">
                            {documents.idCard.name}
                          </span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500">
                            Kimlik, ehliyet veya pasaport yükleyin
                          </span>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    İkametgah Belgesi
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      id="addressProof"
                      accept="image/*,.pdf"
                      onChange={handleFileUpload('addressProof')}
                      className="hidden"
                    />
                    <label
                      htmlFor="addressProof"
                      className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer ${
                        documents.addressProof
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                          : 'border-gray-300 hover:border-blue-500 dark:border-gray-600 dark:hover:border-blue-400'
                      }`}
                    >
                      {documents.addressProof ? (
                        <>
                          <CheckCircle className="w-8 h-8 text-green-500 mb-2" />
                          <span className="text-sm text-green-600">
                            {documents.addressProof.name}
                          </span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-gray-400 mb-2" />
                          <span className="text-sm text-gray-500">
                            Son 3 aya ait fatura veya ikametgah belgesi
                          </span>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || (!documents.idCard && !documents.addressProof)}
                  className="w-full px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {loading ? 'Yükleniyor...' : 'Belgeleri Gönder'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;